import { VStack } from '@chakra-ui/react';
import type { Dispatch } from 'react';
import type { SelectOption, EditorProps } from '@kula-ai/components';
import { EmailBlockSection } from '../../../components/EmailBlockSection';
import { TemplatedEmail } from '../../../components/TemplatedEmail';
import { IcsAttachmentCheckbox } from '../../../components/IcsAttachmentCheckbox';
import type {
  InterviewCommunicationAction,
  InterviewCommunicationFormState,
  InterviewEditorAttachmentItem,
  InterviewVariablesByCategory,
} from '../../../InterviewActivity/communication/types';
import type { InterviewCommunicationValidationErrors } from '../../../InterviewActivity/communication/validate';
import { SCHEDULE_INTERVIEW_COPY } from '../../config/schedule.copy';
import {
  EmailSentBadge,
  type EmailSentBadgeProps,
} from '../../editMode/EmailSentBadge';

interface CandidatePaneProps {
  state: InterviewCommunicationFormState;
  dispatch: Dispatch<InterviewCommunicationAction>;
  fieldErrors: InterviewCommunicationValidationErrors;
  /** Calendar-invite template options — `candidate_interview_notice`. */
  calendarTemplateOptions: SelectOption[];
  /** Email template options — `candidate_interview_confirmation`. */
  emailTemplateOptions: SelectOption[];
  fromOptions: SelectOption[];
  /** `true` while the delegatable-senders fetch is still resolving —
   *  forwarded to the From Select so it renders a spinner instead of
   *  an empty dropdown. */
  isFromOptionsLoading?: boolean;
  ccBccOptions: SelectOption[];
  calendarVariables: InterviewVariablesByCategory;
  emailVariables: InterviewVariablesByCategory;
  uploadInlineImage?: EditorProps['uploadInlineImage'];
  sentEmailBadge?: EmailSentBadgeProps;
}

export const CandidatePane = ({
  state,
  dispatch,
  fieldErrors,
  calendarTemplateOptions,
  emailTemplateOptions,
  fromOptions,
  isFromOptionsLoading = false,
  ccBccOptions,
  calendarVariables,
  emailVariables,
  uploadInlineImage,
  sentEmailBadge,
}: CandidatePaneProps) => {
  const copy = SCHEDULE_INTERVIEW_COPY.communication;

  return (
    <VStack align="stretch" spacing="4" flex="1" minW={0}>
      <EmailBlockSection
        title={copy.candidate.calendarTitle}
        sectionId="schedule-comm-candidate-calendar"
      >
        <TemplatedEmail
          className="w-full"
          showFrom={false}
          showTo={false}
          showSubject={false}
          enableCcBcc={false}
          showTemplate
          showEditor
          editorVariant="calendarInvite"
          variables={calendarVariables}
          templateOptions={calendarTemplateOptions}
          applyTemplateBodyToEditor
          value={state.candidateCalendar}
          onChange={(value) =>
            dispatch({ type: 'SET_CANDIDATE_CALENDAR', payload: value })
          }
          errors={fieldErrors.candidateCalendar}
          placeholders={{ template: copy.placeholders.template }}
        />
      </EmailBlockSection>

      <EmailBlockSection
        title={copy.candidate.emailTitle}
        sectionId="schedule-comm-candidate-email"
        showSwitch
        switchChecked={state.candidateEmailEnabled}
        onSwitchChange={(checked) =>
          dispatch({ type: 'SET_CANDIDATE_EMAIL_ENABLED', payload: checked })
        }
        suffix={
          sentEmailBadge ? (
            <EmailSentBadge {...sentEmailBadge} showTooltip={false} />
          ) : undefined
        }
      >
        {state.candidateEmailEnabled ? (
          <>
            <TemplatedEmail
              className="w-full"
              showFrom
              showTo={false}
              showSubject
              enableCcBcc
              showTemplate
              showEditor
              editorVariant="richEmail"
              variables={emailVariables}
              attachments={state.candidateEmailAttachments}
              onAttachmentsChange={(items: InterviewEditorAttachmentItem[]) =>
                dispatch({
                  type: 'SET_CANDIDATE_EMAIL_ATTACHMENTS',
                  payload: items,
                })
              }
              templateOptions={emailTemplateOptions}
              fromOptions={fromOptions}
              fromSelectProps={{ loading: isFromOptionsLoading }}
              ccBccOptions={ccBccOptions}
              applyTemplateBodyToEditor
              value={state.candidateEmail}
              onChange={(value) =>
                dispatch({ type: 'SET_CANDIDATE_EMAIL', payload: value })
              }
              errors={fieldErrors.candidateEmail}
              placeholders={{
                from: copy.placeholders.from,
                subject: copy.placeholders.subject,
                template: copy.placeholders.template,
              }}
              uploadInlineImage={uploadInlineImage}
            />
            <IcsAttachmentCheckbox
              checked={state.candidateIcsEnabled}
              onChange={(v) =>
                dispatch({ type: 'SET_CANDIDATE_ICS_ENABLED', payload: v })
              }
            />
          </>
        ) : null}
      </EmailBlockSection>
    </VStack>
  );
};
